import { faBasketShopping } from "@fortawesome/pro-solid-svg-icons";
import { useMutation } from "@tanstack/react-query";

import { toast } from "~/components/hooks/use-toast";
import { basketActions } from "~/providers/store/basket";
import { logger } from "~/services/logger";
import { T } from "@repo/transifex";
import { useBasketContext } from "~/providers/store/basket/context";
import { inferStockStatusFromBasket } from "~/utils/stock/infer-stock-status";

export class BarcodeNotRegisteredError extends Error {
  override name = "BarcodeNotRegisteredError";
  constructor(barcode: string) {
    super("Barcode not registered");
    this.barcode = barcode;
  }
  barcode: string;
}

export class ProductOutOfStockError extends Error {
  override name = "ProductOutOfStockError";
  constructor(productId: string) {
    super("Product is out of stock");
    this.productId = productId;
  }
  productId: string;
}

export class ProductStockLimitReachedError extends Error {
  override name = "ProductStockLimitReachedError";
  constructor(productId: string) {
    super("Product stock limit reached");
    this.productId = productId;
  }
  productId: string;
}

export const useFindProduct = () => {
  const products = useBasketContext((state) => state.products);

  const basket = useBasketContext((state) => state.basket);

  return useMutation({
    mutationFn: async (barcode: string) => {
      const product = products.find((product) =>
        product.barcodeIds.includes(barcode)
      );

      if (!product) {
        throw new BarcodeNotRegisteredError(barcode);
      }

      const stockStatus = inferStockStatusFromBasket({
        product,
        basket
      });

      if (stockStatus === "out-of-stock") {
        throw new ProductOutOfStockError(product.id);
      }

      if (stockStatus === "reserved") {
        throw new ProductStockLimitReachedError(product.id);
      }

      basketActions.addRegular(product.id, !product.discount?.isAllowanceOver);
      return Promise.resolve(product);
    },
    onError: (error, barcode) => {
      if (error instanceof BarcodeNotRegisteredError) {
        logger.warn("[Product Barcode] Barcode not registered", {
          product_barcode: barcode
        });
        return;
      }

      if (error instanceof ProductOutOfStockError) {
        logger.warn("[Product Barcode] Product out of stock", {
          product_barcode: barcode,
          product_id: error.productId
        });
        toast({
          title: <T _str="Sold out" />,
          description: <T _str="This product is currently sold out" />,
          variant: "warning",
          toastDuration: "default"
        });
        return;
      }

      if (error instanceof ProductStockLimitReachedError) {
        logger.warn("[Product Barcode] Stock limit reached", {
          product_barcode: barcode,
          product_id: error.productId
        });
        toast({
          title: <T _str="Stock limit reached" />,
          description: (
            <T _str="This product has reached its stock limit and cannot be added" />
          ),
          variant: "warning",
          toastDuration: "default"
        });
        return;
      }

      logger.error(error, "[Product Barcode] Unknown error", {
        product_barcode: barcode
      });
    },
    onSuccess: (product, barcode) => {
      logger.debug("[Product Barcode] Product added to basket", {
        product_barcode: barcode,
        product_barcodes: product.barcodeIds,
        product_id: product.id
      });
      toast({
        title: product.name,
        description: <T _str="Added to basket" />,
        variant: "success",
        icon: faBasketShopping,
        toastDuration: "default"
      });
    }
  });
};
